// API client for AI_ALM backend.
//
// Kept small & hand-rolled for now — we'll switch to OpenAPI-generated
// types once the /openapi.json contract is frozen. Error envelopes
// follow plan §9.7 (`{ error: { code, message, trace_id } }`).

import type {
  Briefing,
  BriefingDecision,
  CompliancePack,
  ErrorEnvelope,
  Gate,
  GateDecisionRecord,
  GateRewindRequest,
  GoBackResponse,
  HealthResponse,
  InputKind,
  InputListResponse,
  InputRow,
  IntakeAnswers,
  Project,
  ProjectContext,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  RequiredSignatoriesResponse,
  RunStateRecord,
  SignatoriesPatch,
  TemplateResponse,
  TemplateSpec,
  TemplateStatusResponse,
} from '@/types'
import type {
  LabRunRecord,
  LabRunSummary,
  StartLabRunRequest,
} from '@/types/labRuns'

const API_BASE = '/api'

/**
 * 202 envelope returned by every ``POST /phaseN?async=true``. The real
 * outputs land on disk asynchronously; the caller waits for a
 * ``phase_completed`` (or ``error``) event on the WS stream — see
 * ``waitForPhaseCompletion`` in ``lib/phaseEvents``.
 */
export interface PhaseAcceptedResponse {
  project_id: string
  run_id: number
  phase: number
  status: 'accepted'
}

/** One architect decision record in a prior-decisions payload.
 *  Mirrors the ``ArchitectDecision`` dataclass in
 *  ``ai_alm.services.architect_decisions_artifact``. */
export interface PriorDecisionRecord {
  id: string
  origin: string
  question: string
  stakeholder: string
  status: 'resolved' | 'open' | 'deferred' | 'not_applicable'
  decision: string | null
  rationale: string | null
  alternatives_considered: string[]
  deferral_hint: string | null
  decided_at: string | null
  source_gate: string
}

/** Response shape of ``GET /projects/:id/runs/:rid/prior-decisions``. */
export type PriorDecisionsResponse =
  | { available: false }
  | {
      available: true
      prior_run_id: number
      prior_run_generated_at: string
      decisions: PriorDecisionRecord[]
    }

// Multi-user (Phase 6 / R5) — every browser instance gets a stable
// uuid stored in localStorage and attached to every request as
// ``X-Client-Id``. The backend uses it only for log correlation —
// **never** for authorization. See plan/AI_ALM_MultiUser_Implementation_Plan.md
// Phase 6.R5.
const CLIENT_ID_KEY = 'ai_alm:client_id'

function getOrCreateClientId(): string {
  // Some test runners disable localStorage; fall back to a per-page id.
  try {
    const existing = window.localStorage.getItem(CLIENT_ID_KEY)
    if (existing && existing.length > 0) {
      return existing
    }
    // ``crypto.randomUUID`` is supported in every browser we care about
    // (and Vitest's jsdom env).
    const fresh =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `cid-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
    window.localStorage.setItem(CLIENT_ID_KEY, fresh)
    return fresh
  } catch {
    return `eph-${Math.random().toString(36).slice(2)}`
  }
}

/**
 * Per-browser client id, generated once and persisted in localStorage.
 * Exposed for the WS layer (``useProjectEvents``) so the same id
 * appears on both HTTP and WS hops, which makes correlation in the
 * server logs trivial.
 *
 * **Not** an authentication token — never read it server-side for
 * authz decisions. See the plan's R5 note.
 */
export function getClientId(): string {
  return getOrCreateClientId()
}

/**
 * Generate a fresh per-fetch ``X-Request-Id`` (T1 — plan §1.1).
 *
 * Lives one fetch — this is the *narrower* correlation id than
 * ``X-Client-Id`` (which lives one browser). The backend echoes
 * the value on the response so the dev console can confirm the
 * one-request-one-trail contract holds.
 */
function newRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '')
  }
  return `req-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
}

/**
 * Build a file URL for the backend's read-only project-file server
 * (plan §M3.1 — briefing + gate markdown bodies).
 *
 * ``path`` is the path-relative-to-project-dir as returned by the
 * briefing/gate record's ``artifact_path`` field.
 */
export function projectFileUrl(projectId: string, path: string): string {
  return `${API_BASE}/projects/${encodeURIComponent(projectId)}/files/${path}`
}


export class ApiError extends Error {
  code: string
  status: number
  detail: Record<string, unknown>
  trace_id?: string

  constructor(message: string, status: number, envelope?: ErrorEnvelope) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = envelope?.error?.code ?? `http_${status}`
    this.detail = envelope?.error ?? {}
    this.trace_id = envelope?.error?.trace_id
  }
}

async function parseError(response: Response): Promise<ApiError> {
  let envelope: ErrorEnvelope | undefined
  try {
    envelope = (await response.json()) as ErrorEnvelope
  } catch {
    envelope = undefined
  }
  const message =
    envelope?.error?.message ?? response.statusText ?? `HTTP ${response.status}`
  return new ApiError(message, response.status, envelope)
}

/**
 * If a protected request comes back with the backend's
 * ``auth.required`` 401 envelope, navigate to ``/login`` so the user
 * can re-authenticate. The original path is preserved in ``?next=`` so
 * the login page can bounce them back when they're done.
 *
 * Auth endpoints are intentionally skipped so the login form can
 * surface ``auth.invalid_credentials`` as an inline error rather than
 * a navigation, and so the boot-time ``GET /api/auth/session`` doesn't
 * trigger a navigation loop while the auth store is still hydrating.
 */
function maybeRedirectToLogin(path: string, error: ApiError): void {
  if (error.status !== 401 || error.code !== 'auth.required') return
  if (path.startsWith('/auth/')) return
  if (typeof window === 'undefined') return
  if (window.location.pathname === '/login') return
  const next = window.location.pathname + window.location.search
  window.location.href = `/login?next=${encodeURIComponent(next)}`
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    // Session-cookie auth: the backend's ``SessionAuthMiddleware``
    // reads ``ai_alm_session`` off the request. ``credentials:
    // 'include'`` is what gets the browser to attach the cookie.
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // Multi-user (Phase 6 / R5) — server logs the id; never trusted
      // for authz. Order matters: caller-supplied headers should
      // override our defaults so a future test can fake a different
      // client id, hence ``options?.headers`` last.
      'X-Client-Id': getOrCreateClientId(),
      // T1 — per-fetch correlation id. The backend echoes it on the
      // response and stamps every log line emitted while servicing
      // the request with the same id, so a single grep reconstructs
      // one user action's full server-side trail.
      'X-Request-Id': newRequestId(),
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const error = await parseError(response)
    maybeRedirectToLogin(path, error)
    throw error
  }

  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}


/**
 * Bug #21 [v2] — public G4 validation-settings discovery surface.
 * The gate panel calls ``api.getG4Settings()`` on mount so a server-
 * side change to ``rationale_min_chars`` / ``bulk_accept_threshold``
 * / ``max_submit_retries`` propagates without a frontend redeploy.
 * Route: ``GET /api/test/g4-settings`` — see
 * ``src/ai_alm/api/routes/g4_settings.py`` for the response model.
 */
export interface G4SettingsResponse {
  rationale_min_chars: number
  bulk_accept_threshold: number
  max_submit_retries: number
  g4_graceful_degradation: boolean
}

/** ``GET /api/auth/session`` — whoami + whether the gate is on at all. */
export interface SessionInfo {
  user: string | null
  /** False = no LOGIN_USERNAME/PASSWORD configured; SPA can skip /login. */
  auth_enabled: boolean
}

/**
 * Deployment-architecture catalog (Tier 1) — one curated entry per
 * suggestion the wizard's combobox renders. ``rationale_template`` is
 * the static "why this fits" sentence; the LLM ranker (Tier 2) may
 * replace it with project-specific text.
 */
export interface DeploymentCatalogEntry {
  value: string
  label: string
  fit_regulatory: string[]
  fit_shape: string[]
  rationale_template: string
  doc_url: string
}

export interface DeploymentCatalogField {
  field_id: string
  label: string
  description: string
  entries: DeploymentCatalogEntry[]
}

export interface DeploymentCatalogResponse {
  schema_version: string
  context: { shape: string | null; regulatory: string[] }
  fields: DeploymentCatalogField[]
}

/** Body for ``POST /api/intake/suggest-deployment`` (Tier 2). */
export interface SuggestDeploymentBody {
  field_id: string
  problem_statement?: string
  shape?: string | null
  regulatory?: string[]
  max_suggestions?: number
}

/**
 * Tier 2 response. ``tier`` is ``'llm'`` when the LLM ranker
 * returned a usable result, ``'fallback'`` when the endpoint
 * served the deterministic Tier 1 ordering after the LLM failed.
 * The wizard renders both the same way (the rationale string just
 * may be static vs. project-specific).
 */
export interface RankedSuggestion {
  value: string
  label: string
  rationale: string
  doc_url: string
}

export interface SuggestDeploymentResponse {
  tier: 'llm' | 'fallback'
  field_id: string
  field_label: string
  suggestions: RankedSuggestion[]
  warning?: string
}

export const api = {
  // Health
  health: () => request<HealthResponse>('/health'),

  // Session auth — login form, logout button, and the whoami the SPA
  // calls on boot to decide whether to bounce the user to /login.
  auth: {
    session: () => request<SessionInfo>('/auth/session'),
    login: (username: string, password: string) =>
      request<SessionInfo>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }),
    logout: () =>
      request<void>('/auth/logout', { method: 'POST' }),
  },

  // G4 validation settings (Bug #21 [v2]) — config-derived thresholds
  // the gate panel uses to keep its W3.1 / W3.2 pre-submit sanitiser
  // and retry budget in lockstep with the running backend. The
  // backend ships ``Cache-Control: max-age=300`` so the per-mount
  // fetch is cheap; 404 callers should ``??``-fall-back to today's
  // hard-coded constants (20 / 5 / 3) — see ``g4Sanitiser.ts``'s
  // ``*_FALLBACK`` exports.
  getG4Settings: () => request<G4SettingsResponse>('/test/g4-settings'),

  // Projects (plan §4.2)
  listProjects: () => request<Project[]>('/projects'),
  createProject: (data: ProjectCreateRequest) =>
    request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getProject: (projectId: string) => request<Project>(`/projects/${projectId}`),
  updateProject: (projectId: string, data: ProjectUpdateRequest) =>
    request<Project>(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  deleteProject: (projectId: string) =>
    request<void>(`/projects/${projectId}`, { method: 'DELETE' }),

  // Intake (plan §M2.1, §9.13)
  submitIntake: (projectId: string, answers: IntakeAnswers) =>
    request<ProjectContext>(`/projects/${projectId}/intake`, {
      method: 'POST',
      body: JSON.stringify(answers),
    }),
  getIntake: (projectId: string) =>
    request<ProjectContext>(`/projects/${projectId}/intake`),

  // Signatories (plan §3 — Approval Chain editor)
  getRequiredSignatories: (projectId: string) =>
    request<RequiredSignatoriesResponse>(
      `/projects/${projectId}/intake/required-signatories`,
    ),
  patchSignatories: (projectId: string, patch: SignatoriesPatch) =>
    request<ProjectContext>(`/projects/${projectId}/intake/signatories`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  // AI-governance ratification (2026-05-28) — post-intake fallback
  // for PEG-AI-GOVERNANCE-SIGNATURE-001. The Pre-Export Review card
  // posts here when the wizard didn't surface the AI-gov block at
  // intake time but the generated SR text declares an AI-derived
  // output. Backend persists both intake.ai_governance_* AND
  // phase1/ai-governance-decision.json for the existing runs.
  patchAiGovernance: (
    projectId: string,
    body: {
      disposition: 'in_scope' | 'not_applicable'
      ratified_by: string
      rationale?: string | null
    },
  ) =>
    request<ProjectContext>(`/projects/${projectId}/intake/ai-governance`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // Deployment-architecture suggestions (2026-05-28 — Tier 1 + Tier 2
  // for the Smart Intake wizard's "Deployment architecture" step).
  // Tier 1 is a curated catalog filtered by intake context; Tier 2
  // re-ranks one field with an LLM call against the project's
  // problem statement. The endpoints sit at /api/intake/* (NOT under
  // /api/projects/*) because they're project-agnostic — the wizard
  // hits them before a project is created.
  getDeploymentCatalog: (params: {
    shape?: string | null
    regulatory?: string[]
  }) => {
    const qs = new URLSearchParams()
    if (params.shape) qs.set('shape', params.shape)
    if (params.regulatory && params.regulatory.length > 0) {
      qs.set('regulatory', params.regulatory.join(','))
    }
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return request<DeploymentCatalogResponse>(
      `/intake/deployment-catalog${query}`,
    )
  },
  suggestDeployment: (body: SuggestDeploymentBody) =>
    request<SuggestDeploymentResponse>('/intake/suggest-deployment', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // User inputs (uploaded content / formatting docs + free-form text).
  // Backend persists everything under projects/<id>/inputs/ so an audit
  // can reproduce exactly what the user supplied at intake time.
  listInputs: (projectId: string, kind?: InputKind) => {
    const qs = kind ? `?kind=${encodeURIComponent(kind)}` : ''
    return request<InputListResponse>(`/projects/${projectId}/inputs${qs}`)
  },
  uploadInput: async (
    projectId: string,
    kind: 'content' | 'format',
    file: File,
    label?: string,
  ) => {
    const form = new FormData()
    form.append('file', file)
    if (label) form.append('label', label)
    const response = await fetch(
      `${API_BASE}/projects/${encodeURIComponent(projectId)}/inputs?kind=${encodeURIComponent(
        kind,
      )}`,
      { method: 'POST', body: form },
    )
    if (!response.ok) {
      throw await parseError(response)
    }
    return (await response.json()) as InputRow
  },
  addTextInput: (projectId: string, label: string, content: string) =>
    request<InputRow>(`/projects/${projectId}/inputs/text`, {
      method: 'POST',
      body: JSON.stringify({ label, content }),
    }),
  deleteInput: (projectId: string, inputId: string) =>
    request<void>(
      `/projects/${encodeURIComponent(projectId)}/inputs/${encodeURIComponent(inputId)}`,
      { method: 'DELETE' },
    ),

  // Import Refiner — PR-H/I (plan §6). Run the uploaded content
  // through the INCOSE prevention rule subset, splitting compound
  // shall clauses and preserving normative facts. Returns the
  // original + refined text plus diagnostics so the panel can
  // render a side-by-side diff. Synchronous; 5-minute hard cap on
  // the backend. ``refined_text === original_text`` when the flag
  // is off (off-path no-op).
  refineInput: (projectId: string, inputId: string, runId = 1) =>
    request<{
      input_id: string
      original_text: string
      refined_text: string
      elapsed_seconds: number
      timed_out: boolean
      valid: boolean
      repaired_attempts: number
      violations_resolved: number
      missing_fact_diagnostics: Array<{
        requirement_id: string
        missing_facts: string[]
        input_clause: string
      }>
      needs_refinement: boolean
    }>(
      `/projects/${encodeURIComponent(projectId)}/inputs/${encodeURIComponent(inputId)}/refine?run_id=${runId}`,
      { method: 'POST' },
    ),

  // Template extraction (plan §9.10, addendum §10.13)
  uploadTemplate: async (projectId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    const response = await fetch(`${API_BASE}/projects/${projectId}/template`, {
      method: 'POST',
      body: form,
    })
    if (!response.ok) {
      throw await parseError(response)
    }
    return (await response.json()) as TemplateResponse
  },
  getTemplate: (projectId: string) =>
    request<TemplateResponse>(`/projects/${projectId}/template`),
  getTemplateStatus: (projectId: string) =>
    request<TemplateStatusResponse>(`/projects/${projectId}/template/status`),
  putTemplate: (projectId: string, spec: TemplateSpec) =>
    request<TemplateResponse>(`/projects/${projectId}/template`, {
      method: 'PUT',
      body: JSON.stringify({ template_spec: spec }),
    }),
  deleteTemplate: (projectId: string) =>
    request<void>(`/projects/${projectId}/template`, { method: 'DELETE' }),

  // Phase briefings (plan §M3.1)
  createBriefing: (projectId: string, runId: number, phase: number) =>
    request<Briefing>(
      `/projects/${projectId}/runs/${runId}/briefings/${phase}`,
      { method: 'POST' },
    ),
  getBriefing: (projectId: string, runId: number, phase: number) =>
    request<Briefing>(
      `/projects/${projectId}/runs/${runId}/briefings/${phase}`,
    ),
  submitBriefingDecision: (
    projectId: string,
    runId: number,
    phase: number,
    decision: BriefingDecision,
  ) =>
    request<Briefing>(
      `/projects/${projectId}/runs/${runId}/briefings/${phase}/decision`,
      { method: 'POST', body: JSON.stringify(decision) },
    ),

  // Phase runners (plan §M2.4, §M4.1, §M4.2, §M4.4)
  // Phase 1 uses ``?async=true`` like phases 2-5: the POST returns 202 +
  // ``{status: 'accepted'}`` immediately and the caller awaits the
  // ``phase_completed`` (or ``error``) WS event via
  // ``waitForPhaseCompletion``. The legacy synchronous POST held the
  // connection open for the whole multi-minute LLM run, which the GKE
  // load balancer killed at its 30s backend timeout (502).
  runPhase1: (
    projectId: string,
    runId: number,
    body?: {
      problem_statement?: string
      go_back_reason?: string
      // Architect's answers to G1 clarifying questions, keyed by
      // question id (CQ-1, CQ-2, …) parsed from intent-map.md §4.
      // When provided the Contextualist re-runs and bakes the
      // answers into the refined charter (plan §M3.2 / brain/13-hitl).
      clarifying_answers?: Record<string, string>
    },
  ) =>
    request<PhaseAcceptedResponse>(
      `/projects/${projectId}/runs/${runId}/phase1?async=true`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  // Phase 2/3/4/5 run endpoints use ``?async=true`` so the POST
  // returns 202 + ``{status: 'accepted'}`` immediately. The caller
  // (``PipelineRunner``) then awaits a ``phase_completed`` or
  // ``error`` event on the WS stream via ``waitForPhaseCompletion``.
  // This avoids the long-running HTTP request that Chrome was killing
  // around the 5-minute mark — see comments in
  // ``src/ai_alm/api/routes/phases.py`` (async-phase plumbing).
  runPhase2: (
    projectId: string,
    runId: number,
    body?: { artifacts?: Array<Record<string, unknown>> },
  ) =>
    request<PhaseAcceptedResponse>(
      `/projects/${projectId}/runs/${runId}/phase2?async=true`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  // Phase 2.5 — persist the architect-confirmed compliance pack to disk
  // before the swarm runs. Without this call the G2.5 Accept / Edit /
  // Drop decisions live only in the browser's Zustand store, and the
  // pack on disk stays at the Phase-2.5-runner's ``drafted`` state.
  // The "Proceed to Phase 3" CTA in ComplianceTriageSurface calls this
  // with the full pack from the store, then navigates to the swarm.
  saveCompliancePack: (
    projectId: string,
    runId: number,
    pack: CompliancePack,
  ) =>
    request<CompliancePack>(
      `/projects/${projectId}/runs/${runId}/phase2_5/compliance-pack`,
      { method: 'POST', body: JSON.stringify(pack) },
    ),
  runPhase3: (projectId: string, runId: number) =>
    request<PhaseAcceptedResponse>(
      `/projects/${projectId}/runs/${runId}/phase3?async=true`,
      { method: 'POST' },
    ),
  runPhase4: (projectId: string, runId: number) =>
    request<PhaseAcceptedResponse>(
      `/projects/${projectId}/runs/${runId}/phase4?async=true`,
      { method: 'POST' },
    ),
  // Phase 5 keeps a synchronous upfront gate-enforcement check on the
  // server (so the architect's "Run Phase 5" click surfaces a 409 with
  // the unresolved decisions inline). The Exporter work itself moves
  // to a background task once gates clear.
  runPhase5: (projectId: string, runId: number) =>
    request<PhaseAcceptedResponse>(
      `/projects/${projectId}/runs/${runId}/phase5?async=true`,
      { method: 'POST' },
    ),
  applyRewrites: (
    projectId: string,
    runId: number,
    approvedRewriteIds: string[],
  ) =>
    request<{
      project_id: string
      run_id: number
      applied: number
      skipped: number
      conflicts: Array<Record<string, unknown>>
      files_touched: string[]
    }>(`/projects/${projectId}/runs/${runId}/rewrites/apply`, {
      method: 'POST',
      body: JSON.stringify({ approved_rewrite_ids: approvedRewriteIds }),
    }),
  // Phase E (Agent Evaluation Implementation Plan §6.3) — materialise
  // per-finding rewrites for the architect-selected ids. Each proposal
  // is persisted to ``runs/<n>/phase4/rewrites/<F-###>.md`` with YAML
  // frontmatter + corrected statement body. Empty selection returns a
  // no-op bundle without firing any LLM call. The G4 review surface
  // calls this before submitting decisions so the side-by-side diff
  // can show real "after" text instead of relying on the bundled
  // audit-diff (which now ships findings only, no rewrite proposals).
  proposeRewrites: (
    projectId: string,
    runId: number,
    selectedIds: string[],
  ) =>
    request<{
      project_id: string
      run_id: number
      rewrites_dir: string
      summary_path: string
      proposal_count: number
      rewrite_paths: Record<string, string>
      proposals: Array<{
        finding_id: string
        rewrite_id: string
        requirement_id: string
        rule: string
        severity: string
        // Phase-E rewrite-status badge (option 1):
        //   "auto_fixed"   — re-audit clean for the targeted rule(s)
        //   "unresolved"   — AFTER == BEFORE or rule still fires
        //   "needs_review" — fixed but possible regression / drift
        status: 'auto_fixed' | 'unresolved' | 'needs_review'
        status_reason: string
      }>
    }>(`/projects/${projectId}/runs/${runId}/rewrites/propose`, {
      method: 'POST',
      body: JSON.stringify({ selected_ids: selectedIds }),
    }),


  // Gates (plan §M3.2)
  createGate: (projectId: string, runId: number, gateNumber: number) =>
    request<Gate>(`/projects/${projectId}/runs/${runId}/gates/${gateNumber}`, {
      method: 'POST',
    }),
  getGate: (projectId: string, runId: number, gateNumber: number) =>
    request<Gate>(
      `/projects/${projectId}/runs/${runId}/gates/${gateNumber}`,
    ),
  /**
   * Fetch architect decisions captured in the most recent prior run.
   * Powers the G1 "Import all" banner (Architect Decisions Downstream
   * Flow Plan §8 — cross-run carry-forward). Always 200; ``available
   * === false`` means there's no prior run with closed G1 to inherit
   * from.
   */
  getPriorDecisions: (projectId: string, runId: number) =>
    request<PriorDecisionsResponse>(
      `/projects/${projectId}/runs/${runId}/prior-decisions`,
    ),
  // Submit gate decisions. If the gate JSON is missing on disk (404 —
  // gate was never emitted on this run, or the project was reset under
  // a stale localStorage draft), transparently re-emit the gate and
  // retry once. Without this guard the architect sees a confusing
  // "404 Not Found" toast in the console even though their decisions
  // have legitimate IDs the server would accept post-emit. createGate
  // is idempotent (it overwrites the gate file with the canonical
  // payload), so the retry is safe.
  submitGateDecisions: async (
    projectId: string,
    runId: number,
    gateNumber: number,
    decisions: GateDecisionRecord[],
  ): Promise<Gate> => {
    const path = `/projects/${projectId}/runs/${runId}/gates/${gateNumber}/decisions`
    try {
      return await request<Gate>(path, {
        method: 'POST',
        body: JSON.stringify(decisions),
      })
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        await request<Gate>(
          `/projects/${projectId}/runs/${runId}/gates/${gateNumber}`,
          { method: 'POST' },
        )
        return await request<Gate>(path, {
          method: 'POST',
          body: JSON.stringify(decisions),
        })
      }
      throw err
    }
  },
  gateGoBack: (
    projectId: string,
    runId: number,
    gateNumber: number,
    body: GateRewindRequest,
  ) =>
    request<GoBackResponse>(
      `/projects/${projectId}/runs/${runId}/gates/${gateNumber}/go_back`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // Artifacts (plan §4.2)
  getArtifact: (projectId: string, runId: number, artifactType: string) =>
    request<{ path: string; content: string }>(
      `/projects/${projectId}/runs/${runId}/artifacts/${artifactType}`,
    ),

  // Run state (resume after reboot — see brain/14)
  //
  // ``GET /state`` returns an empty record when no state has been
  // persisted yet (the runner hasn't fired any phase events for this
  // project), so callers don't need a 404 path. ``PUT /state/focus``
  // persists the user's last surface so the Command Center can re-
  // open on the same screen after a reload or reboot.
  getRunState: (projectId: string, runId: number) =>
    request<RunStateRecord>(`/projects/${projectId}/runs/${runId}/state`),
  setRunFocus: (projectId: string, runId: number, focus: string | null) =>
    request<RunStateRecord>(
      `/projects/${projectId}/runs/${runId}/state/focus`,
      { method: 'PUT', body: JSON.stringify({ focus }) },
    ),

  // Test Lab (T10 — plan §10.2). Routes are mounted only when
  // ``AI_ALM_TEST_LAB_ENABLED=1`` on the backend; the SPA
  // surfaces 404s as a "Test Lab is not enabled on this server"
  // empty state.
  testRuns: {
    list: () => request<LabRunSummary[]>(`/test-runs`),
    get: (runId: string) =>
      request<LabRunRecord>(`/test-runs/${encodeURIComponent(runId)}`),
    start: (body: StartLabRunRequest) =>
      request<LabRunSummary>(`/test-runs`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    stop: (runId: string) =>
      request<{ id: string; status: string }>(
        `/test-runs/${encodeURIComponent(runId)}/stop`,
        { method: 'POST' },
      ),
  },
}

// --------------------------------------------------------------------------- //
// WebSocket connection manager (plan §4.3)
// --------------------------------------------------------------------------- //
let ws: WebSocket | null = null
const listeners = new Set<(event: MessageEvent) => void>()

export function connectWebSocket(onMessage: (event: MessageEvent) => void) {
  listeners.add(onMessage)

  if (!ws) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    ws = new WebSocket(`${protocol}//${window.location.host}/ws`)

    ws.onmessage = (event) => {
      listeners.forEach((listener) => listener(event))
    }

    ws.onclose = () => {
      ws = null
    }
  }

  return () => {
    listeners.delete(onMessage)
    if (listeners.size === 0 && ws) {
      ws.close()
      ws = null
    }
  }
}

export function sendWSMessage(message: object) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(message))
  }
}
